import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import API from '../../data/api';
import CustomTextField from '../custom/CustomTextField';
import CustomTablePagination from '../custom/CustomTablePagination';

const SUPPORT_PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

const SUPPORT_TAG_OPTIONS = [
  'Bug',
  'Feature request',
  'Billing',
  'Data issue',
  'Integration',
  'UI/UX',
  'Access',
];

const INITIAL_SUPPORT_TICKET = {
  title: '',
  description: '',
  priority: 'medium',
  category: '',
};

const toTitleCase = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());

const normalizePriority = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'high' || normalized === 'medium' || normalized === 'low') {
    return normalized;
  }
  return 'medium';
};

const priorityChipColor = (priority = '') => {
  if (priority === 'high') {
    return 'error';
  }
  if (priority === 'medium') {
    return 'warning';
  }
  return 'success';
};

const formatSupportDate = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const normalizeSupportRows = (rows = []) => {
  const normalizedRows = rows.map((row, index) => {
    const priority = normalizePriority(row?.priority);
    const title = String(row?.title || row?.subject || '').trim();
    const description = String(row?.description || row?.message || '').trim();
    const category = String(row?.category || row?.tag || '').trim();
    const status = String(row?.status || 'Open').trim();
    const createdAt = row?.created_at || row?.updated_at || row?.inserted_at || null;
    const id = row?.id || row?.ticket_id || `${title || 'ticket'}-${createdAt || index}-${index}`;

    return {
      id,
      title: title || 'Untitled request',
      description: description || '-',
      priority,
      category: category || 'General',
      status: status || 'Open',
      created_at: createdAt,
    };
  });

  return normalizedRows.sort((a, b) => {
    const left = new Date(a.created_at || 0).getTime();
    const right = new Date(b.created_at || 0).getTime();
    return right - left;
  });
};

const normalizeSupportResponse = (payload = {}, fallback = {}) => {
  const nestedData = payload?.data && typeof payload.data === 'object' ? payload.data : {};
  const rows = Array.isArray(nestedData?.rows)
    ? nestedData.rows
    : Array.isArray(payload?.rows)
      ? payload.rows
      : [];
  const page = Number(nestedData?.page) || Number(fallback.page) || 1;
  const records = Number(nestedData?.records) || Number(fallback.records) || 10;
  const totalRecords = Number(nestedData?.total_records);

  return {
    rows: normalizeSupportRows(rows),
    page,
    records,
    totalRecords: Number.isFinite(totalRecords) ? totalRecords : rows.length,
  };
};

const validateSupportTicket = (ticket) => {
  const errors = {};
  if (!ticket.title.trim()) {
    errors.title = 'A brief title is required.';
  } else if (ticket.title.trim().length < 5) {
    errors.title = 'Use at least 5 characters.';
  }

  if (!ticket.description.trim()) {
    errors.description = 'Please describe your request.';
  } else if (ticket.description.trim().length < 20) {
    errors.description = 'Use at least 20 characters for better context.';
  }

  if (!ticket.priority) {
    errors.priority = 'Select a priority.';
  }

  if (!ticket.category) {
    errors.category = 'Select a category.';
  }

  return errors;
};

export default function TechnicalDifficulties() {
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(10);
  const [recordsList] = useState([5, 10, 15, 20]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [openSupportModal, setOpenSupportModal] = useState(false);
  const [supportTicket, setSupportTicket] = useState({ ...INITIAL_SUPPORT_TICKET });
  const [supportTicketErrors, setSupportTicketErrors] = useState({});
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportSubmitSuccess, setSupportSubmitSuccess] = useState('');
  const [supportSubmitError, setSupportSubmitError] = useState('');

  const fetchSupportTickets = useCallback(async () => {
    setTicketsLoading(true);
    setTicketsError('');

    try {
      const response = await API.get(`settings/support?page=${page}&records=${records}`);
      const normalizedResponse = normalizeSupportResponse(response?.data, {
        page,
        records,
      });

      setSupportTickets(normalizedResponse.rows);
      setTotalRecords(normalizedResponse.totalRecords);
      setTotalPages(Math.max(1, Math.ceil(normalizedResponse.totalRecords / records)));
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      setSupportTickets([]);
      setTicketsError('Unable to load technical difficulties yet. You can still submit new requests.');
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setTicketsLoading(false);
    }
  }, [page, records]);

  useEffect(() => {
    fetchSupportTickets();
  }, [fetchSupportTickets]);

  const prioritySummary = useMemo(() => {
    return supportTickets.reduce(
      (summary, ticket) => {
        summary[ticket.priority] = (summary[ticket.priority] || 0) + 1;
        return summary;
      },
      { low: 0, medium: 0, high: 0 },
    );
  }, [supportTickets]);

  const handleNextPage = (action) => {
    setPage((previousPage) => {
      if (action === 'next') {
        return Math.min(previousPage + 1, totalPages);
      }
      return Math.max(previousPage - 1, 1);
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const nextRecords = parseInt(event.target.value, 10);
    setRecords(nextRecords);
    setPage(1);
  };

  const resetSupportTicketForm = () => {
    setSupportTicket({ ...INITIAL_SUPPORT_TICKET });
    setSupportTicketErrors({});
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    setSupportSubmitting(false);
  };

  const handleOpenSupportModal = () => {
    resetSupportTicketForm();
    setOpenSupportModal(true);
  };

  const handleCloseSupportModal = () => {
    setOpenSupportModal(false);
    resetSupportTicketForm();
  };

  const handleSupportFieldChange = (event) => {
    const { name, value } = event.target;
    setSupportTicket((previous) => ({
      ...previous,
      [name]: value,
    }));
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    setSupportTicketErrors((previous) => ({
      ...previous,
      [name]: '',
    }));
  };

  const handleSupportTicketSubmit = async (event) => {
    event.preventDefault();
    const normalizedTicket = {
      ...supportTicket,
      title: supportTicket.title.trim(),
      description: supportTicket.description.trim(),
      priority: normalizePriority(supportTicket.priority),
    };

    const validationErrors = validateSupportTicket(normalizedTicket);
    setSupportTicketErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setSupportSubmitting(true);
    setSupportSubmitSuccess('');
    setSupportSubmitError('');
    try {
      const payload = {
        title: normalizedTicket.title,
        message: normalizedTicket.description,
        priority: toTitleCase(normalizedTicket.priority),
        category: normalizedTicket.category,
      };

      await API.post('settings/support', payload);

      handleCloseSupportModal();
      if (page === 1) {
        fetchSupportTickets();
      } else {
        setPage(1);
      }
    } catch (error) {
      console.error('Error submitting support request:', error);
      setSupportSubmitError('Failed to submit support request. Please try again.');
      setSupportTicket(normalizedTicket);
    } finally {
      setSupportSubmitting(false);
    }
  };

  return (
    <>
      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.3,
              background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 62%, #0ea5e9 100%)',
              color: '#ffffff',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Technical Difficulties
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.4 }}>
                  Track support requests raised from logistics workflows.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleOpenSupportModal}
                sx={{
                  bgcolor: '#ffffff',
                  color: '#0f172a',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#e2e8f0' },
                }}
              >
                Contact support
              </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 1.6 }} useFlexGap flexWrap="wrap">
              <Chip
                label={`High ${prioritySummary.high}`}
                size="small"
                sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 700 }}
              />
              <Chip
                label={`Medium ${prioritySummary.medium}`}
                size="small"
                sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 700 }}
              />
              <Chip
                label={`Low ${prioritySummary.low}`}
                size="small"
                sx={{ bgcolor: '#DCFCE7', color: '#166534', fontWeight: 700 }}
              />
              <Chip
                label={`Total ${totalRecords}`}
                size="small"
                sx={{ bgcolor: '#E2E8F0', color: '#0F172A', fontWeight: 700 }}
              />
            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1.5, md: 2 } }}>
            {!!ticketsError && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {ticketsError}
              </Alert>
            )}

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: '#e2e8f0',
              }}
            >
              {ticketsLoading ? (
                <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          '& th': {
                            backgroundColor: '#f8fafc',
                            fontWeight: 700,
                            borderBottomColor: '#e2e8f0',
                          },
                        }}
                      >
                        <TableCell>Title</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Priority</TableCell>
                        <TableCell align="center">Category</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right">Submitted</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {supportTickets.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No technical difficulties have been logged yet.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}

                      {supportTickets.map((ticket) => (
                        <TableRow
                          key={`support-row-${ticket.id}`}
                          hover
                          sx={{
                            '& td': {
                              borderBottomColor: '#e2e8f0',
                            },
                          }}
                        >
                          <TableCell sx={{ minWidth: 180 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {ticket.title}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 320, maxWidth: 520 }}>
                            <Tooltip title={ticket.description}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2,
                                  overflow: 'hidden',
                                }}
                              >
                                {ticket.description}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              color={priorityChipColor(ticket.priority)}
                              label={toTitleCase(ticket.priority)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip size="small" variant="outlined" label={ticket.category} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              variant={String(ticket.status).toLowerCase() === 'open' ? 'filled' : 'outlined'}
                              color={String(ticket.status).toLowerCase() === 'open' ? 'primary' : 'default'}
                              label={ticket.status}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            {formatSupportDate(ticket.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            <CustomTablePagination
              rowsPerPage={records}
              recordsList={recordsList}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleNextPage={handleNextPage}
              currentPage={page}
              totalPages={totalPages}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={openSupportModal}
        onClose={handleCloseSupportModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Contact support</DialogTitle>
        <Box component="form" onSubmit={handleSupportTicketSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Alert severity="info">
                Include clear business impact and steps to reproduce if this is a bug.
              </Alert>

              <CustomTextField
                required
                fullWidth
                name="title"
                label="Brief title"
                value={supportTicket.title}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.title)}
                helperText={supportTicketErrors.title || 'Example: Fuel sales chart missing for March 2026'}
                inputProps={{ maxLength: 120 }}
              />

              <CustomTextField
                required
                fullWidth
                multiline
                minRows={4}
                name="description"
                label="Describe your request"
                value={supportTicket.description}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.description)}
                helperText={
                  supportTicketErrors.description
                  || `${supportTicket.description.length}/2000 characters`
                }
                inputProps={{ maxLength: 2000 }}
              />

              <CustomTextField
                required
                select
                fullWidth
                name="priority"
                label="Priority"
                value={supportTicket.priority}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.priority)}
                helperText={supportTicketErrors.priority || 'Choose urgency based on business impact.'}
              >
                {SUPPORT_PRIORITY_OPTIONS.map((priorityOption) => (
                  <MenuItem key={`support-priority-${priorityOption.value}`} value={priorityOption.value}>
                    {priorityOption.label}
                  </MenuItem>
                ))}
              </CustomTextField>

              <CustomTextField
                required
                select
                fullWidth
                name="category"
                label="Category"
                value={supportTicket.category}
                onChange={handleSupportFieldChange}
                error={Boolean(supportTicketErrors.category)}
                helperText={supportTicketErrors.category || 'Select the single category that best describes the request.'}
              >
                {SUPPORT_TAG_OPTIONS.map((tagOption) => (
                  <MenuItem key={`support-category-${tagOption}`} value={tagOption}>
                    {tagOption}
                  </MenuItem>
                ))}
              </CustomTextField>

              {!!supportSubmitError && (
                <Alert severity="error">
                  {supportSubmitError}
                </Alert>
              )}

              {!!supportSubmitSuccess && (
                <Alert severity="success">
                  {supportSubmitSuccess}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 1.8 }}>
            <Button onClick={handleCloseSupportModal} disabled={supportSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={supportSubmitting}>
              {supportSubmitting ? 'Submitting...' : 'Submit request'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
